<template>
  <v-container fluid>
    <v-card class="pa-6">
      <!-- Header -->
      <v-row align="center" class="mb-4">
        <v-col>
          <div class="text-h5 font-weight-bold">
            + New Matter Data Sheet
            <span v-if="!isIndividual">
              | เพิ่มแบบฟอร์มสรุปข้อมูลนิติบุคคล</span>
            <span v-else> | บุคคลธรรมดา</span>
          </div>
          <div class="caption grey--text">
            Entity Type: <strong>{{ entityTypeLabel }}</strong>
          </div>
        </v-col>
        <v-col class="d-flex justify-end">
          <v-btn outlined @click="$router.back()">
            <v-icon left>mdi-arrow-left</v-icon>ย้อนกลับ
          </v-btn>
        </v-col>
      </v-row>

      <!-- ==================== INDIVIDUAL FORM ==================== -->
      <div v-if="isIndividual">
        <v-card outlined class="pa-6" style="max-width:780px;">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.client_name" label="Client Name | ชื่อลูกค้า *" outlined dense
                placeholder="ชื่อ–นามสกุล" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field :value="entityTypeLabel" label="Entity Type | ประเภทลูกค้า *" outlined dense readonly
                background-color="grey lighten-4"></v-text-field>
            </v-col>
          </v-row>

          <div class="form-section-title mb-3">👤 INDIVIDUAL INFORMATION</div>

          <!-- ID Type -->
          <div class="mb-4">
            <div class="caption font-weight-medium mb-2">
              ประเภทเอกสารระบุตัวตน *
            </div>
            <v-row>
              <v-col cols="12" md="6">
                <div :class="[
                  'radio-option',
                  form.id_type === 'national_id' ? 'radio-selected' : '',
                ]" @click="form.id_type = 'national_id'">
                  <v-icon class="mr-2">{{
                    form.id_type === "national_id"
                      ? "mdi-radiobox-marked"
                      : "mdi-radiobox-blank"
                  }}</v-icon>
                  <div>
                    <span class="font-weight-medium">🪪 บัตรประชาชน</span>
                    <div class="caption grey--text">National ID</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div :class="[
                  'radio-option',
                  form.id_type === 'passport' ? 'radio-selected' : '',
                ]" @click="form.id_type = 'passport'">
                  <v-icon class="mr-2">{{
                    form.id_type === "passport"
                      ? "mdi-radiobox-marked"
                      : "mdi-radiobox-blank"
                  }}</v-icon>
                  <div>
                    <span class="font-weight-medium">🛂 หนังสือเดินทาง</span>
                    <div class="caption grey--text">Passport</div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-3">
              <v-col cols="12" md="6">
                <v-text-field v-model="form.full_name" label="Full Name | ชื่อ-นามสกุล *" outlined dense
                  placeholder="ชื่อ–นามสกุลจริง" :rules="[rules.required]"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.nationality" label="Nationality | สัญชาติ" outlined dense></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- National ID -->
          <v-row v-if="form.id_type === 'national_id'">
            <v-col cols="12" md="12">
              <v-text-field v-model="form.national_id" label="เลขบัตรประชาชน (13 หลัก) *" outlined dense
                placeholder="X-XXXX-XXXXX-XX-X" maxlength="17" :rules="[nationalIdRule]"
                @input="formatNationalId"></v-text-field>
            </v-col>
          </v-row>

          <!-- Passport -->
          <v-row v-else>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.passport_no" label="เลขที่หนังสือเดินทาง *" outlined dense
                placeholder="e.g. AA1234567" maxlength="15" :rules="[rules.required]"></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field v-model="form.contact_address" label="Contact Address | ที่อยู่สำหรับติดต่อ" outlined
                dense></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.contact_number" label="Contact Number | เบอร์โทรศัพท์" outlined dense
                placeholder="0X-XXXX-XXXX"></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.contact_email" label="Contact Email | อีเมล" outlined dense
                type="email"></v-text-field>
            </v-col>
          </v-row>

          <!-- Actions -->
          <v-row class="mt-4">
            <v-col class="d-flex justify-end" style="gap:10px;">
              <v-btn color="#059669" dark :loading="loadingSave" @click="saveAndCreateDocket">
                <v-icon left>mdi-content-save</v-icon>Save &amp; Create Docket
              </v-btn>
              <v-btn color="#0A7EA5" dark :loading="loadingSave" @click="saveOnly">
                <v-icon left>mdi-content-save</v-icon>Save
              </v-btn>
              <v-btn outlined @click="$router.back()">Cancel</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </div>

      <!-- ==================== COMPANY FORM ==================== -->
      <div v-else>
        <!-- Client Name & Entity Type -->
        <v-row class="mb-2">
          <v-col cols="12" md="6">
            <v-autocomplete v-model="selectedClient" :items="clientList" item-text="text" item-value="value"
              label="Client Name | ชื่อลูกค้า *" outlined dense placeholder="-- เลือกลูกค้า / Select Client --"
              clearable hide-no-data return-object :rules="[rules.required]" @change="onClientChange"
              @click:clear="onClientClear"></v-autocomplete>
            <div class="caption orange--text">
              ดึงรายชื่อจาก Client Directory — พิมพ์เพื่อค้นหา
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field :value="entityTypeLabel" label="Entity Type | ประเภทลูกค้า *" outlined dense readonly
              background-color="grey lighten-4"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section 1: Company Information -->
        <div class="form-section-title mb-3">
          🏢 COMPANY INFORMATION | ข้อมูลนิติบุคคล
        </div>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.company_name" label="Company Name | ชื่อบริษัท *" outlined dense
              :rules="[rules.required]"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.registration_no" label="Registration No. *" outlined dense
              :rules="[rules.required]"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.date_of_incorporation" label="Date of Incorporation *" outlined dense
              type="date" :rules="[rules.required]"></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.registered_address" label="Registered Address *" outlined dense
              :rules="[rules.required]"></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.branch" label="Branch" outlined dense></v-text-field>
          </v-col>
        </v-row>

        <!-- Capital -->
        <div class="form-section-title mb-3 mt-2">💰 CAPITAL</div>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field v-model="form.registered_capital" label="Registered Capital (THB)" outlined dense
              type="number"></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="form.number_of_shares" label="Number of Shares" outlined dense
              type="number"></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="form.par_value_per_share" label="Par Value per Share" outlined dense
              type="number"></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="form.paid_up_capital" label="Paid-up Capital (THB)" outlined dense
              type="number"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section 3: Shareholders -->
        <div class="form-section-title mb-3 mt-2">
          👥 SHAREHOLDERS | ผู้ถือหุ้น
        </div>
        <v-simple-table dense class="mb-2" outlined>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="bg-colorth tabletextwhite">FULL NAME | ชื่อ–นามสกุล</th>
                <th class="bg-colorth tabletextwhite">NATIONALITY | สัญชาติ</th>
                <th class="bg-colorth tabletextwhite text-right num-cell">ORDINARY SHARES</th>
                <th class="bg-colorth tabletextwhite text-right num-cell">Shares Percentage</th>
                <th class="bg-colorth tabletextwhite text-right num-cell">PREFERRED SHARES</th>
                <th class="bg-colorth tabletextwhite" style="width:50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sh, i) in form.shareholders" :key="'sh' + i">
                <td>
                  <v-text-field v-model="sh.full_name" dense hide-details solo flat
                    placeholder="ชื่อ–นามสกุล"></v-text-field>
                </td>
                <td>
                  <v-text-field v-model="sh.nationality" dense hide-details solo flat
                    placeholder="สัญชาติ"></v-text-field>
                </td>

                <td class="num-cell">
                  <v-text-field v-model="sh.ordinary_shares" dense hide-details solo flat type="number" placeholder="0"
                    @focus="activeShareholderIndex = i"></v-text-field>
                </td>
                <td class="num-cell text-right">{{ formatPercent(ownershipPercentages[i]) }}</td>
                <td class="num-cell">
                  <v-text-field v-model="sh.preferred_shares" dense hide-details solo flat type="number"
                    placeholder="0"></v-text-field>
                </td>
                <td style="width:50px;">
                  <v-btn icon x-small color="red" @click="removeShareholder(i)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="shareholder-total-row">
                <td></td>
                <td class="text-right font-weight-bold">TOTAL | รวม</td>
                <td class="num-cell text-right font-weight-bold"
                  :class="isOrdinarySharesExceeded ? 'red--text' : 'green--text'">
                  {{ totalOrdinaryShares }} / {{ form.number_of_shares || 0 }}
                </td>
                <td class="num-cell text-right font-weight-bold">{{ formatPercent(totalPercentage) }}</td>
                <td class="num-cell text-right font-weight-bold">{{ totalPreferredShares }}</td>
                <td style="width:50px;"></td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
        <v-btn outlined small @click="addShareholder" class="mb-4">
          <v-icon left small>mdi-plus</v-icon>Add Shareholder
        </v-btn>

        <!-- Section 4: Directors -->
        <div class="form-section-title mb-3 mt-2">
          👔 DIRECTORS | กรรมการบริษัท
        </div>
        <v-simple-table dense class="mb-2" outlined>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="bg-colorth tabletextwhite">
                  DIRECTORS FULL NAME | ชื่อกรรมการ
                </th>
                <th class="bg-colorth tabletextwhite">NATIONALITY | สัญชาติ</th>
                <th class="bg-colorth tabletextwhite">
                  AUTHORIZED DIRECTOR | กรรมการผู้มีอำนาจ
                </th>
                <th class="bg-colorth tabletextwhite" style="width:50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(dir, i) in form.directors" :key="'dir' + i">
                <td>
                  <v-text-field v-model="dir.full_name" dense hide-details solo flat
                    placeholder="ชื่อ–นามสกุล"></v-text-field>
                </td>
                <td>
                  <v-text-field v-model="dir.nationality" dense hide-details solo flat
                    placeholder="สัญชาติ"></v-text-field>
                </td>
                <td>
                  <v-select v-model="dir.authorized" :items="['Yes', 'No']" dense hide-details solo flat
                    placeholder="-- เลือก --" clearable></v-select>
                </td>
                <td>
                  <v-btn icon x-small color="red" @click="removeDirector(i)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-btn outlined small @click="addDirector" class="mb-4">
          <v-icon left small>mdi-plus</v-icon>Add Director
        </v-btn>

        <!-- Section: Authority Directors -->
        <div class="form-section-title mb-3 mt-2">
          📋 AUTHORITY DIRECTORS
        </div>
        <v-row>
          <v-col cols="12">
            <v-textarea v-model="form.authority_directors_detail" label="Authority Directors details" outlined dense
              auto-grow rows="3" row-height="24"></v-textarea>
          </v-col>
        </v-row>

        <!-- Section 5: Auditor -->
        <div class="form-section-title mb-3 mt-2">📋 AUDITOR | ผู้สอบบัญชี</div>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.audit_firm" label="Audit Firm | ชื่อบริษัทผู้สอบบัญชี" outlined
              dense></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.licensed_auditor_name" label="Licensed Auditor's Name" outlined
              dense></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.auditor_license_no" label="Auditor's License No." outlined dense></v-text-field>
          </v-col>

        </v-row>
        <v-row class="mt-n5">
          <v-col cols="12" md="4">
            <!-- ⭐ เพิ่มใหม่ -->
            <v-text-field v-model="form.fiscal_year_start" label="Fiscal Start Year | รอบปีบัญชีเริ่มต้น" outlined dense
              type="date"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.fiscal_year_end" label="Fiscal Year End | รอบปีบัญชี" outlined dense
              type="date"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.auditor_national_id" label="Auditor's National ID (13 digits)" outlined dense
              placeholder="X-XXXX-XXXXX-XX-X" maxlength="17" hint="กรอกเฉพาะตัวเลข 13 หลัก" persistent-hint
              @input="formatAuditorNationalId"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section 6: Accountant -->
        <div class="form-section-title mb-3 mt-2">
          📊 ACCOUNTANT | ผู้ทำบัญชีของบริษัท
        </div>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.accountant_firm" label="Accountant Firm | ชื่อบริษัทผู้ทำบัญชี" outlined
              dense></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.accountant_name" label="Accountant Name | ชื่อผู้ทำบัญชี" outlined
              dense></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.accountant_national_id" label="Accountant's National ID (13 digits)" outlined
              dense placeholder="X-XXXX-XXXXX-XX-X" maxlength="17" hint="กรอกเฉพาะตัวเลข 13 หลัก" persistent-hint
              @input="formatAccountantNationalId"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section 7: VAT -->
        <div class="form-section-title mb-3 mt-2">
          🧾 VAT REGISTRATION | การขึ้นทะเบียนภาษีมูลค่าเพิ่ม (ถ้ามี)
        </div>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.vat_issued_on" label="VAT Issued on | วันที่ออกทะเบียนภาษีมูลค่าเพิ่ม" outlined
              dense type="date"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section 8: Foreign Business -->
        <div class="form-section-title mb-3 mt-2">
          🌐 FOREIGN BUSINESS | บริษัทต่างด้าว
        </div>
        <v-card outlined class="pa-3 mb-3" :class="form.is_foreign_business ? 'blue lighten-5' : ''"
          style="cursor:pointer;" @click="form.is_foreign_business = !form.is_foreign_business">
          <div class="d-flex align-center">
            <v-checkbox v-model="form.is_foreign_business" hide-details class="ma-0 pa-0 mr-3" @click.stop></v-checkbox>
            <div>
              <div class="font-weight-medium" style="font-size:13px;">
                บริษัทต่างด้าว / Foreign Business
              </div>
              <div class="caption grey--text">
                กาเครื่องหมายหากบริษัทเป็นกิจการที่คนต่างด้าวถือหุ้นเกิน 49%
              </div>
            </div>
          </div>
        </v-card>

        <v-card v-if="form.is_foreign_business" outlined class="pa-4 mb-4 blue lighten-5">
          <div class="font-weight-bold blue--text mb-3">
            📎 เอกสารบริษัทต่างด้าว — กรุณาอัปโหลดให้ครบทั้ง 2 รายการ
            (อัปโหลดได้มากกว่า 1 ไฟล์ต่อรายการ)
          </div>

          <!-- 3.1 Foreign Business License (หลายไฟล์) -->
          <div class="mb-5">
            <div class="caption font-weight-medium mb-2">
              3.1 ใบอนุญาตประกอบธุรกิจคนต่างด้าว *
              <span class="grey--text font-weight-regular">Foreign Business License</span>
            </div>

            <v-btn outlined small @click="$refs.fblInputAdd.click()">
              <v-icon left small>mdi-upload</v-icon>เลือกไฟล์ (เลือกได้หลายไฟล์)
            </v-btn>
            <input ref="fblInputAdd" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style="display:none;"
              @change="onFblFilesSelected" />
            <div class="caption grey--text mt-1">
              PDF, JPG, PNG — ไม่เกิน 25 MB ต่อไฟล์
            </div>

            <div v-for="(f, idx) in form.foreign_business_license_files" :key="'fbl-new-' + idx"
              class="file-preview-box mt-2">
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center" style="gap:8px;">
                  <v-icon :color="isPdf(f.name) ? 'red darken-1' : 'blue darken-1'">
                    {{ isPdf(f.name) ? "mdi-file-pdf-box" : "mdi-file-image" }}
                  </v-icon>
                  <div class="caption font-weight-medium">{{ f.name }}</div>
                </div>
                <v-btn icon x-small color="red" @click="removeFblFile(idx)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <!-- 3.2 Foreign Business Certificate (หลายไฟล์) -->
          <div>
            <div class="caption font-weight-medium mb-2">
              3.2 หนังสือรับรองประกอบธุรกิจคนต่างด้าว *
              <span class="grey--text font-weight-regular">Foreign Business Certificate</span>
            </div>

            <v-btn outlined small @click="$refs.fbcInputAdd.click()">
              <v-icon left small>mdi-upload</v-icon>เลือกไฟล์ (เลือกได้หลายไฟล์)
            </v-btn>
            <input ref="fbcInputAdd" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style="display:none;"
              @change="onFbcFilesSelected" />
            <div class="caption grey--text mt-1">
              PDF, JPG, PNG — ไม่เกิน 25 MB ต่อไฟล์
            </div>

            <div v-for="(f, idx) in form.foreign_business_certificate_files" :key="'fbc-new-' + idx"
              class="file-preview-box mt-2">
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center" style="gap:8px;">
                  <v-icon :color="isPdf(f.name) ? 'red darken-1' : 'blue darken-1'">
                    {{ isPdf(f.name) ? "mdi-file-pdf-box" : "mdi-file-image" }}
                  </v-icon>
                  <div class="caption font-weight-medium">{{ f.name }}</div>
                </div>
                <v-btn icon x-small color="red" @click="removeFbcFile(idx)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <v-alert dense outlined class="mt-3 caption business-rule-alert">
            <strong>Business Rule:</strong> ต้องอัปโหลดเอกสารทั้ง 2 รายการ
            (อย่างน้อย 1 ไฟล์ต่อรายการ) ก่อน Save
          </v-alert>
        </v-card>

        <!-- Actions -->
        <v-row class="mt-4">
          <v-col class="d-flex justify-end" style="gap:10px;">
            <v-btn color="#059669" dark :loading="loadingSave" @click="saveAndCreateDocket">
              <v-icon left>mdi-content-save</v-icon>Save &amp; Create Docket
            </v-btn>
            <v-btn color="#0A7EA5" dark :loading="loadingSave" @click="saveOnly">
              <v-icon left>mdi-content-save</v-icon>Save
            </v-btn>
            <v-btn outlined @click="$router.back()">Cancel</v-btn>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />
    <SuccessDialogPush :status="dialogAddPush" :text_color="text_color" :title="title" :message="message"
      :link="link" />
  </v-container>
</template>

<script>
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import { server } from "@/services/constants";
import { createIncorporateMatter } from "@/services/api-dtime/api_incorporate";
import { getAllCustomerListByCompany } from "@/services/api-dtime/api_customer";

export default {
  name: "incorporate-matter-add",
  components: { SuccessDialog, SuccessDialogPush },
  data() {
    return {
      entityType: "",
      clientList: [],
      selectedClient: null,
      loadingSave: false,
      activeShareholderIndex: null,
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && String(v).trim() !== "") ||
          "กรุณากรอกข้อมูล",
      },

      form: {
        client_id: null,
        client_name: "",
        client_name_en: "",
        entity_type: "",
        // individual
        id_type: "national_id",
        full_name: "",
        nationality: "",
        national_id: "",
        passport_no: "",
        contact_address: "",
        contact_number: "",
        contact_email: "",
        // company
        company_name: "",
        registration_no: "",
        date_of_incorporation: "",
        registered_address: "",
        branch: "",
        authority_directors_detail: "",
        registered_capital: 0,
        number_of_shares: 0,
        par_value_per_share: 0,
        paid_up_capital: 0,
        shareholders: [
          {
            full_name: "",
            nationality: "",
            ordinary_shares: 0,
            preferred_shares: 0,
          },
        ],
        directors: [{ full_name: "", nationality: "", authorized: "" }],
        audit_firm: "",
        licensed_auditor_name: "",
        auditor_license_no: "",
        fiscal_year_start: "",
        fiscal_year_end: "",
        auditor_national_id: "",
        accountant_firm: "",
        accountant_name: "",
        accountant_national_id: "",
        vat_issued_on: "",
        is_foreign_business: false,
        foreign_business_license_files: [],
        foreign_business_certificate_files: [],
      },

      dialogAdd: false,
      dialogAddPush: false,
      text_color: "text-h5 green--text text-center",
      title: "",
      message: "",
      link: "",
    };
  },
  computed: {
    isIndividual() {
      return this.entityType === "Individual";
    },
    entityTypeLabel() {
      const map = {
        "Limited Company": "Limited Company | บริษัทจำกัด",
        "Public Company Limited": "Public Company Limited | บริษัทมหาชนจำกัด",
        Foundation: "Foundation | มูลนิธิ",
        "Representative Office": "Representative Office | สำนักงานผู้แทนการค้า",
        Individual: "Individual | บุคคลธรรมดา",
      };
      return map[this.entityType] || this.entityType;
    },
    totalOrdinaryShares() {
      return this.form.shareholders.reduce(
        (sum, sh) => sum + (Number(sh.ordinary_shares) || 0),
        0,
      );
    },
    totalPreferredShares() {
      return this.form.shareholders.reduce(
        (sum, sh) => sum + (Number(sh.preferred_shares) || 0),
        0,
      );
    },
    isOrdinarySharesExceeded() {
      return (
        Number(this.form.number_of_shares) > 0 &&
        this.totalOrdinaryShares > Number(this.form.number_of_shares)
      );
    },
    ownershipPercentages() {
      const base = Number(this.form.number_of_shares) || 0;
      const n = this.form.shareholders.length;
      if (!base || !n) return this.form.shareholders.map(() => 0);

      const SCALE = 100000; // 5 ตำแหน่งทศนิยม
      const raw = this.form.shareholders.map(
        (sh) => ((Number(sh.ordinary_shares) || 0) / base) * 100 * SCALE,
      );

      const floors = raw.map((r) => Math.floor(r));
      const target = Math.round(raw.reduce((a, b) => a + b, 0));
      let remainder = target - floors.reduce((a, b) => a + b, 0);

      const order = raw
        .map((r, i) => ({ i, frac: r - Math.floor(r) }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i);

      const units = floors.slice();
      for (let k = 0; k < remainder; k++) {
        units[order[k % n].i] += 1;
      }

      return units.map((u) => u / SCALE);
    },
    totalPercentage() {
      return this.ownershipPercentages.reduce((a, b) => a + b, 0);
    },
  },
  watch: {
    "form.number_of_shares"() {
      this.clampOrdinaryShares();
    },
    "form.shareholders": {
      deep: true,
      handler() {
        this.clampOrdinaryShares();
      },
    },
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    this.$hideLoader();
    this.entityType = decodeURIComponent(this.$route.params.entity_type || "");
    this.form.entity_type = this.entityType;
    await this.loadClients();
  },
  methods: {
    formatPercent(v) {
      return (Number(v) || 0).toFixed(5) + "%";
    },
    // ปรับ ordinary_shares อัตโนมัติเมื่อผลรวมเกิน number_of_shares
    // โดยลดค่าแถวสุดท้ายลงให้ผลรวมพอดี และเคลียร์ทั้งตารางถ้า number_of_shares = 0
    // ใช้ _adjusting กัน watcher วนเรียกตัวเองซ้ำตอน method นี้แก้ค่าที่ watch อยู่
    clampOrdinaryShares() {
      if (this._adjusting) return;
      if (!this.form.shareholders.length) return;

      const numShares = Number(this.form.number_of_shares) || 0;
      const total = this.form.shareholders.reduce(
        (sum, sh) => sum + (Number(sh.ordinary_shares) || 0),
        0,
      );

      // เมื่อ Number of Shares เป็น 0 → รีเซ็ต ordinary_shares กลับเป็น 0
      // ใช้ debounce 800ms รอให้พิมพ์เสร็จก่อน ไม่งั้นจะรีเซ็ตทุกตัวอักษรจนพิมพ์ไม่ได้
      if (numShares === 0) {
        if (total > 0) {
          clearTimeout(this._zeroResetTimer);
          this._zeroResetTimer = setTimeout(() => {
            this._adjusting = true;
            this.form.shareholders.forEach((sh) => {
              sh.ordinary_shares = 0;
            });
            this.$nextTick(() => {
              this._adjusting = false;
            });
            this.setupAlertDialog(
              true,
              "แจ้งเตือน",
              "Number of Shares เป็น 0 — ระบบได้ปรับ Ordinary Shares ของผู้ถือหุ้นทุกคนกลับเป็น 0 กรุณากรอก Number of Shares ก่อน",
              "text-h5 orange--text text-center",
            );
          }, 800);
        }
        return;
      }

      clearTimeout(this._zeroResetTimer); // ยกเลิกถ้าผู้ใช้กรอก Number of Shares ทัน

      if (total <= numShares) return;

      // ⭐ ปรับ "แถวที่ผู้ใช้เพิ่งแก้" (จาก @focus) ถ้าไม่มีให้ fallback เป็นแถวสุดท้าย
      // เช่น กรณีแก้ Number of Shares ให้น้อยลงจนตารางเดิมเกิน
      const idx =
        this.activeShareholderIndex !== null &&
          this.activeShareholderIndex < this.form.shareholders.length
          ? this.activeShareholderIndex
          : this.form.shareholders.length - 1;

      this._adjusting = true;
      const targetRow = this.form.shareholders[idx];
      const othersTotal = total - (Number(targetRow.ordinary_shares) || 0);
      const adjusted = Math.max(numShares - othersTotal, 0);
      targetRow.ordinary_shares = adjusted;
      this.$nextTick(() => {
        this._adjusting = false;
      });

      this.setupAlertDialog(
        true,
        "แจ้งเตือน",
        `ผลรวม Ordinary Shares เกิน Number of Shares (${numShares}) — ระบบได้ปรับค่าแถวที่ ${idx + 1} เป็น ${adjusted} ให้อัตโนมัติ`,
        "text-h5 orange--text text-center",
      );
    },
    async loadClients() {
      try {
        const company_id = localStorage.getItem(server.COMPANYID);
        const res = await getAllCustomerListByCompany(company_id);
        const customers = res.data || [];
        this.clientList = customers.map((c) => ({
          text: c.customer_name || c.name || `Customer #${c.id}`,
          value: Number(c.id),
          raw: c,
        }));
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    },

    onClientChange(selected) {
      if (selected && selected.value) {
        this.form.client_id = selected.value;
        this.form.client_name = selected.text;
        if (!this.isIndividual) {
          this.form.company_name = selected.text; // ⭐ auto-fill Company Name จาก Client ที่เลือก
        }
      } else {
        this.form.client_id = null;
        this.form.client_name = "";
      }
    },

    onClientClear() {
      this.selectedClient = null;
      this.form.client_id = null;
      this.form.client_name = "";
      if (!this.isIndividual) {
        this.form.company_name = ""; // ⭐ เคลียร์ Company Name ด้วยเมื่อ clear client
      }
    },

    nationalIdRule(v) {
      if (!v) return "Required";
      const digits = v.replace(/-/g, "");
      return digits.length === 13 || "กรุณากรอกเลขบัตรประชาชนให้ครบ 13 หลัก";
    },

    formatThaiNationalId(rawValue) {
      let d = (rawValue || "").replace(/\D/g, "").slice(0, 13);
      let result = d;
      if (d.length > 1) result = d[0] + "-" + d.slice(1);
      if (d.length > 5)
        result = result.slice(0, 2) + d.slice(1, 5) + "-" + d.slice(5);
      if (d.length > 10)
        result =
          result.slice(0, 12) + "-" + d.slice(10, 12) + "-" + d.slice(12);
      return result;
    },

    formatNationalId() {
      this.form.national_id = this.formatThaiNationalId(this.form.national_id);
    },

    formatAuditorNationalId() {
      this.form.auditor_national_id = this.formatThaiNationalId(
        this.form.auditor_national_id,
      );
    },

    formatAccountantNationalId() {
      this.form.accountant_national_id = this.formatThaiNationalId(
        this.form.accountant_national_id,
      );
    },

    addShareholder() {
      this.form.shareholders.push({
        full_name: "",
        nationality: "",
        ordinary_shares: 0,
        preferred_shares: 0,
      });
    },
    removeShareholder(i) {
      this.form.shareholders.splice(i, 1);
      this.activeShareholderIndex = null;   // ⭐ กัน index ค้างชี้ไปแถวที่ไม่มีแล้ว
    },
    addDirector() {
      this.form.directors.push({
        full_name: "",
        nationality: "",
        authorized: "",
      });
    },
    removeDirector(i) {
      this.form.directors.splice(i, 1);
    },

    isPdf(filename) {
      return filename?.toLowerCase().endsWith(".pdf");
    },
    onFblFilesSelected(e) {
      const files = Array.from(e.target.files || []);
      this.form.foreign_business_license_files.push(...files);
      e.target.value = "";
    },
    onFbcFilesSelected(e) {
      const files = Array.from(e.target.files || []);
      this.form.foreign_business_certificate_files.push(...files);
      e.target.value = "";
    },
    removeFblFile(idx) {
      this.form.foreign_business_license_files.splice(idx, 1);
    },
    removeFbcFile(idx) {
      this.form.foreign_business_certificate_files.splice(idx, 1);
    },

    validateForm() {
      if (this.isIndividual) {
        if (!this.form.client_name.trim()) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Client Name",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (!this.form.full_name.trim()) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Full Name",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (this.form.id_type === "national_id") {
          const nidCheck = this.nationalIdRule(this.form.national_id);
          if (nidCheck !== true) {
            this.setupAlertDialog(
              true,
              "Check information",
              nidCheck,
              "text-h5 red--text text-center",
            );
            return false;
          }
        } else {
          if (!this.form.passport_no || !this.form.passport_no.trim()) {
            this.setupAlertDialog(
              true,
              "Check information",
              "กรุณากรอกเลขที่หนังสือเดินทาง",
              "text-h5 red--text text-center",
            );
            return false;
          }
        }
      } else {
        if (!this.selectedClient || !this.form.client_id) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณาเลือก Client Name",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (!this.form.company_name.trim()) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Company Name",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (!this.form.registration_no || !this.form.registration_no.trim()) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Registration No.",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (!this.form.date_of_incorporation) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Date of Incorporation",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (
          !this.form.registered_address ||
          !this.form.registered_address.trim()
        ) {
          this.setupAlertDialog(
            true,
            "Check information",
            "กรุณากรอก Registered Address",
            "text-h5 red--text text-center",
          );
          return false;
        }
        if (this.form.is_foreign_business) {
          if (this.form.foreign_business_license_files.length === 0) {
            this.setupAlertDialog(
              true,
              "Check information",
              "กรุณาอัปโหลดใบอนุญาตประกอบธุรกิจคนต่างด้าวอย่างน้อย 1 ไฟล์",
              "text-h5 red--text text-center",
            );
            return false;
          }
          if (this.form.foreign_business_certificate_files.length === 0) {
            this.setupAlertDialog(
              true,
              "Check information",
              "กรุณาอัปโหลดหนังสือรับรองประกอบธุรกิจคนต่างด้าวอย่างน้อย 1 ไฟล์",
              "text-h5 red--text text-center",
            );
            return false;
          }
        }
      }
      return true;
    },

    buildFormData() {
      const fd = new FormData();
      const company_id = localStorage.getItem(server.COMPANYID);
      const user_id = localStorage.getItem(server.USER_ID);

      if (this.form.client_id) {
        fd.append("client_id", this.form.client_id);
      }

      const fields = [
        "client_name",
        "client_name_en",
        "entity_type",
        "nationality",
        "registration_no",
        "company_name",
        "date_of_incorporation",
        "registered_address",
        "branch",
        "authority_directors_detail",
        "audit_firm",
        "licensed_auditor_name",
        "auditor_license_no",
        "fiscal_year_start",
        "fiscal_year_end",
        "auditor_national_id",
        "accountant_firm",
        "accountant_name",
        "accountant_national_id",
        "vat_issued_on",
        "id_type",
        "full_name",
        "national_id",
        "passport_no",
        "contact_address",
        "contact_number",
        "contact_email",
      ];
      fields.forEach((key) => {
        if (
          this.form[key] !== null &&
          this.form[key] !== undefined &&
          this.form[key] !== ""
        ) {
          fd.append(key, this.form[key]);
        }
      });

      fd.append(
        "registered_capital",
        parseFloat(this.form.registered_capital) || 0,
      );
      fd.append("number_of_shares", parseInt(this.form.number_of_shares) || 0);
      fd.append(
        "par_value_per_share",
        parseFloat(this.form.par_value_per_share) || 0,
      );
      fd.append("paid_up_capital", parseFloat(this.form.paid_up_capital) || 0);

      fd.append("company_id", company_id);
      fd.append("user_create", user_id);
      fd.append("status", "Active");
      fd.append(
        "is_foreign_business",
        this.form.is_foreign_business ? "true" : "false",
      );

      const validShareholders = this.form.shareholders.filter(
        (s) => s.full_name.trim() !== "",
      );
      fd.append("shareholders", JSON.stringify(validShareholders));

      const validDirectors = this.form.directors.filter(
        (d) => d.full_name.trim() !== "",
      );
      fd.append("directors", JSON.stringify(validDirectors));

      fd.append(
        "fbl_count",
        String(this.form.foreign_business_license_files.length),
      );
      fd.append(
        "fbc_count",
        String(this.form.foreign_business_certificate_files.length),
      );
      this.form.foreign_business_license_files.forEach((file) => {
        fd.append("file", file);
      });
      this.form.foreign_business_certificate_files.forEach((file) => {
        fd.append("file", file);
      });

      return fd;
    },

    async saveOnly() {
      if (!this.validateForm()) return;
      this.loadingSave = true;
      this.$showLoader();
      try {
        const fd = this.buildFormData();
        await createIncorporateMatter(fd);
        this.$hideLoader();
        this.loadingSave = false;
        this.$store.state.global_push_dialog = true;
        this.setupAlertDialogPush(
          true,
          "Success",
          "บันทึกข้อมูลสำเร็จ",
          "text-h5 green--text text-center",
          "/incorporate-matter-list",
        );
      } catch (error) {
        this.$hideLoader();
        this.loadingSave = false;
        console.error("Error creating matter:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "เกิดข้อผิดพลาดในการบันทึก",
          "text-h5 red--text text-center",
        );
      }
    },

    async saveAndCreateDocket() {
      if (!this.validateForm()) return;
      this.loadingSave = true;
      this.$showLoader();
      try {
        const fd = this.buildFormData();
        const res = await createIncorporateMatter(fd);
        this.$hideLoader();
        this.loadingSave = false;
        const matterId = res.data?.id || res.data?.data?.id;
        this.$router.push(
          `/incorporate-docket-add?matter_id=${matterId || ""}`,
        );
      } catch (error) {
        this.$hideLoader();
        this.loadingSave = false;
        console.error("Error creating matter:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "เกิดข้อผิดพลาดในการบันทึก",
          "text-h5 red--text text-center",
        );
      }
    },

    // ⭐ FIX: คอมโพเนนต์ SuccessDialog.vue ผูก v-model กับ
    // $store.state.global_dialog โดยตรง (ไม่ได้ใช้ "status" prop ที่ส่งเข้าไป
    // เลยแม้แต่จุดเดียว) ดังนั้นถ้าตั้งแค่ this.dialogAdd = status เฉย ๆ
    // popup จะไม่เด้งขึ้นมาเลย ต้อง sync เข้า store ทุกครั้งที่เรียกใช้
    // (บั๊กเดียวกับที่พบและแก้ไปแล้วใน IncorporateDocketAdd.vue —
    // เพิ่มบรรทัดนี้ให้ตรงกัน เพื่อให้ popup แจ้งเตือนเด้งขึ้นจริง
    // เวลากรอกข้อมูลที่มี * ไม่ครบ)
    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
      this.$store.state.global_dialog = status; // ⭐ ขาดอันนี้ทำให้ dialog ไม่เด้ง
    },
    setupAlertDialogPush(status, title, message, text_color, link) {
      this.title = title;
      this.message = message;
      this.dialogAddPush = status;
      this.text_color = text_color;
      this.link = link;
      this.$store.state.global_push_dialog = status; // ⭐ กันพลาดถ้าเรียกซ้ำโดยไม่ set store เอง
    },
  },
};
</script>

<style scoped>
.bg-colorth {
  background-color: #8d8d8d !important;
}

.tabletextwhite {
  color: white !important;
}

.form-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  border-left: 4px solid #0a7ea5;
  padding-left: 10px;
  margin-top: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.radio-option:hover {
  border-color: #0a7ea5;
  background: #f0f9ff;
}

.radio-selected {
  border-color: #0a7ea5;
  background: #eff6ff;
}

.file-preview-box {
  border: 1.5px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 14px;
  background: #eff6ff;
}

.business-rule-alert {
  background-color: #fffbeb !important;
  border: 1.5px solid #fbbf24 !important;
  color: #92400e !important;
}

.business-rule-alert>>>.v-alert__content {
  color: #92400e !important;
}

.business-rule-alert strong {
  color: #78350f !important;
}

/* จัดคอลัมน์ตัวเลขให้ชิดขวาตรงแนวกันทั้ง tbody และ tfoot */
/* จัดคอลัมน์ตัวเลขให้ชิดขวาตรงแนวกันทั้ง tbody และ tfoot */
.num-cell {
  padding-right: 16px !important;
}

.num-cell>>>input {
  text-align: right;
  padding-right: 0 !important;
}

/* รีเซ็ต padding ภายในของ v-text-field (solo flat) ที่ดันตัวเลขเข้ามาจากขอบขวา
   ทำให้ไม่ตรงแนวกับ tfoot ซึ่งเป็น text ธรรมดา */
.num-cell>>>.v-input__slot {
  padding: 0 !important;
}

.num-cell>>>.v-text-field__slot {
  padding-right: 0 !important;
}

.shareholder-total-row {
  background-color: #f3f4f6;
}

.shareholder-total-row td {
  border-top: 2px solid #d1d5db !important;
}
</style>